import React, { useRef, useState } from 'react';
import NiceModal, { antdModalV5, useModal } from '@ebay/nice-modal-react';

import { OrbitMutationOutcome } from 'api/orbit';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import OrbitThemeScope from '../../OrbitThemeScope';
import { TOrbit } from '../../i18n/TModules';
import {
  Popup,
  POPUP_SCALE,
  PopupCancel,
  PopupConfirm,
  PopupError,
  PopupLine
} from './OrbitChatPopup';

export interface IOrbitDeleteChatModalProps {
  /** The chat's title as the lists and the header show it. */
  title: string;
  /** Runs the DELETE; the popup stays open with an inline error when the outcome is not `ok`. */
  onConfirm: () => Promise<OrbitMutationOutcome<null>>;
  /**
   * Runs once the popup has closed after a delete, to place focus: the trigger the popup hands it
   * back to went with the chat.
   */
  onDeleted: () => void;
}

/** "Delete chat?". Resolves `true` once the chat is deleted and `false` on any dismissal. */
const OrbitDeleteChatModal = NiceModal.create(
  ({ title, onConfirm, onDeleted }: IOrbitDeleteChatModalProps) => {
    const modal = useModal();
    const modalProps = antdModalV5(modal);
    const { t } = useMultiLanguageTranslation();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // A second click can land before the re-render that disables the button.
    const inFlight = useRef(false);
    const deleted = useRef(false);

    const handleDelete = async () => {
      if (inFlight.current) {
        return;
      }

      inFlight.current = true;
      setSubmitting(true);
      setError(null);

      const outcome = await onConfirm();

      inFlight.current = false;
      setSubmitting(false);

      if (outcome.kind === 'ok') {
        deleted.current = true;
        modal.resolve(true);
        modal.hide();

        return;
      }

      setError(outcome.message ?? t(`${TOrbit}.page.deleteChat.failed`));
    };

    // Every dismissal waits for the DELETE: closing mid-flight would report a cancel on a chat that
    // is then deleted.
    const handleCancel = () => {
      if (inFlight.current) {
        return;
      }

      modal.resolve(false);
      modal.hide();
    };

    return (
      <Popup
        {...modalProps}
        afterClose={() => {
          modalProps.afterClose();
          if (deleted.current) {
            onDeleted();
          }
        }}
        // Portalled to `document.body`, so it re-declares the tokens the content reads.
        modalRender={node => <OrbitThemeScope>{node}</OrbitThemeScope>}
        title={t(`${TOrbit}.page.deleteChat.title`)}
        width={POPUP_SCALE.width}
        centered
        closable={false}
        maskClosable={!submitting}
        keyboard={!submitting}
        onCancel={handleCancel}
        footer={[
          <PopupCancel key="cancel" disabled={submitting} onClick={handleCancel}>
            {t(`${TOrbit}.actions.cancel`)}
          </PopupCancel>,
          <PopupConfirm
            key="delete"
            type="primary"
            loading={submitting}
            aria-busy={submitting}
            onClick={handleDelete}
          >
            {t(`${TOrbit}.page.deleteChat.confirm`)}
          </PopupConfirm>
        ]}
        data-testid="orbit-delete-chat"
      >
        <PopupLine>{t(`${TOrbit}.page.deleteChat.body`, { title })}</PopupLine>
        {error !== null ? (
          <PopupError role="alert" data-testid="orbit-delete-chat-error">
            {error}
          </PopupError>
        ) : null}
      </Popup>
    );
  }
);

export default OrbitDeleteChatModal;
